/**
 * ITT-972: Engage inbox for Increment 1a profile processing failures.
 * Sibling to BeerIngestionFailuresInbox — clearly labeled as 1a, not scrape/monitor.
 */

// skipcq: JS-W1028
import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { formatRelativeCheckTime } from '../../utils/brewery/beerIngestionMonitoring';
import {
  BEER_PROFILE_PROCESSING_FAILURE_CATEGORIES,
  BEER_PROFILE_PROCESSING_FAILURE_STAGES,
  PROFILE_PROCESSING_FILTER_ALL,
  formatBeerProfileProcessingFailureCategoryLabel,
  formatBeerProfileProcessingFailureStageLabel,
  isProfileFailureRetryDisabled,
  primaryRetryLabelForProfileFailureCategory,
  profileFailureRetryHelperCopy,
  truncateProfileFailureError,
} from '../../utils/brewery/beerProfileProcessingFailures';
import BeerProfileProcessingPulseStrip from './BeerProfileProcessingPulseStrip';

function DispositionChip({ disposition }) {
  const label = disposition === 'resolved' ? 'Resolved' : 'Open';
  const color = disposition === 'resolved' ? 'success' : 'warning';
  return <Chip size="small" color={color} label={label} />;
}

DispositionChip.propTypes = {
  disposition: PropTypes.string.isRequired,
};

function DetailField({ label, children }) {
  return (
    <Typography variant="body2">
      <strong>{label}:</strong> {children}
    </Typography>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function FailureDetailActions({
  item,
  actionLoading,
  pendingItemId,
  onRetry,
  onResolve,
}) {
  const handleRetryClick = useCallback(() => {
    onRetry(item).catch(() => undefined);
  }, [item, onRetry]);

  const handleResolveClick = useCallback(() => {
    onResolve(item.itemId).catch(() => undefined);
  }, [item, onResolve]);

  const retryDisabled = isProfileFailureRetryDisabled(item) || actionLoading;

  return (
    <Stack direction="row" spacing={1}>
      <Button variant="contained" disabled={retryDisabled} onClick={handleRetryClick}>
        {pendingItemId === item.itemId && actionLoading
          ? 'Retrying…'
          : primaryRetryLabelForProfileFailureCategory(item.category)}
      </Button>
      {item.disposition === 'open' ? (
        <Button variant="outlined" disabled={actionLoading} onClick={handleResolveClick}>
          Resolve
        </Button>
      ) : null}
    </Stack>
  );
}

FailureDetailActions.propTypes = {
  item: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
};

function FailureDetailBody({
  item,
  actionLoading,
  pendingItemId,
  onRetry,
  onResolve,
}) {
  return (
    <Box sx={{ width: { xs: 320, sm: 420 }, p: 3 }} role="presentation">
      <Stack spacing={2}>
        <Typography variant="h6">Profile processing failure</Typography>
        <Typography variant="caption" color="text.secondary">
          Increment 1a — observation retain / shadow rebuild / legacy seed. Not Inc 1
          scrape failures.
        </Typography>
        {item.breweryId ? (
          <DetailField label="Brewery">
            <Link component={RouterLink} to={`/brewery/${item.breweryId}`}>
              {item.breweryName || item.breweryId}
            </Link>
          </DetailField>
        ) : null}
        {item.beerId ? <DetailField label="Beer">{item.beerId}</DetailField> : null}
        {item.candidateKey ? (
          <DetailField label="Candidate">{item.candidateKey}</DetailField>
        ) : null}
        <DetailField label="Category">
          {formatBeerProfileProcessingFailureCategoryLabel(item.category)}
        </DetailField>
        <DetailField label="Stage">
          {formatBeerProfileProcessingFailureStageLabel(item.failureStage)}
        </DetailField>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          <strong>Error:</strong> {item.errorMessage || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {profileFailureRetryHelperCopy(item.category)}
        </Typography>
        {item.runId ? <DetailField label="Run">{item.runId}</DetailField> : null}
        <DetailField label="Failures">{item.failureCount ?? 1}</DetailField>
        <FailureDetailActions
          item={item}
          actionLoading={actionLoading}
          pendingItemId={pendingItemId}
          onRetry={onRetry}
          onResolve={onResolve}
        />
      </Stack>
    </Box>
  );
}

FailureDetailBody.propTypes = {
  item: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
};

function FailureDetailDrawer({
  item,
  open,
  onClose,
  onRetry,
  onResolve,
  actionLoading,
  pendingItemId,
}) {
  if (!item) {
    return null;
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <FailureDetailBody
        item={item}
        actionLoading={actionLoading}
        pendingItemId={pendingItemId}
        onRetry={onRetry}
        onResolve={onResolve}
      />
    </Drawer>
  );
}

FailureDetailDrawer.propTypes = {
  item: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
};

function FilterSelect({ labelId, label, value, onChange, children, minWidth }) {
  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select labelId={labelId} label={label} value={value} onChange={onChange}>
        {children}
      </Select>
    </FormControl>
  );
}

FilterSelect.propTypes = {
  labelId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  minWidth: PropTypes.number.isRequired,
};

function FilterBar({
  disposition,
  category,
  failureStage,
  onDispositionChange,
  onCategoryChange,
  onStageChange,
}) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <FilterSelect
        labelId="profile-failure-disposition-label"
        label="Disposition"
        value={disposition}
        onChange={onDispositionChange}
        minWidth={140}
      >
        <MenuItem value="open">Open</MenuItem>
        <MenuItem value="resolved">Resolved</MenuItem>
      </FilterSelect>
      <FilterSelect
        labelId="profile-failure-category-label"
        label="Category"
        value={category}
        onChange={onCategoryChange}
        minWidth={200}
      >
        <MenuItem value={PROFILE_PROCESSING_FILTER_ALL}>All categories</MenuItem>
        {BEER_PROFILE_PROCESSING_FAILURE_CATEGORIES.map((categoryValue) => (
          <MenuItem key={categoryValue} value={categoryValue}>
            {formatBeerProfileProcessingFailureCategoryLabel(categoryValue)}
          </MenuItem>
        ))}
      </FilterSelect>
      <FilterSelect
        labelId="profile-failure-stage-label"
        label="Stage"
        value={failureStage}
        onChange={onStageChange}
        minWidth={180}
      >
        <MenuItem value={PROFILE_PROCESSING_FILTER_ALL}>All stages</MenuItem>
        {BEER_PROFILE_PROCESSING_FAILURE_STAGES.map((stageValue) => (
          <MenuItem key={stageValue} value={stageValue}>
            {formatBeerProfileProcessingFailureStageLabel(stageValue)}
          </MenuItem>
        ))}
      </FilterSelect>
    </Stack>
  );
}

FilterBar.propTypes = {
  disposition: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  failureStage: PropTypes.string.isRequired,
  onDispositionChange: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onStageChange: PropTypes.func.isRequired,
};

function BreweryBeerCell({ row, onBreweryLinkClick }) {
  return (
    <Stack spacing={0.25}>
      {row.breweryId ? (
        <Link
          component={RouterLink}
          to={`/brewery/${row.breweryId}`}
          onClick={onBreweryLinkClick}
        >
          {row.breweryName || row.breweryId}
        </Link>
      ) : (
        <Typography variant="body2">—</Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {row.beerId || row.candidateKey || '—'}
      </Typography>
    </Stack>
  );
}

BreweryBeerCell.propTypes = {
  row: PropTypes.object.isRequired,
  onBreweryLinkClick: PropTypes.func.isRequired,
};

function DispositionActionsCell({
  row,
  actionLoading,
  onResolveClick,
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <DispositionChip disposition={row.disposition} />
      {row.disposition === 'open' ? (
        <Button
          size="small"
          data-item-id={row.itemId}
          onClick={onResolveClick}
          disabled={actionLoading}
        >
          Resolve
        </Button>
      ) : null}
    </Stack>
  );
}

DispositionActionsCell.propTypes = {
  row: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  onResolveClick: PropTypes.func.isRequired,
};

function FailureTableRow({
  row,
  actionLoading,
  pendingItemId,
  onTableRowClick,
  onBreweryLinkClick,
  onRetryClick,
  onResolveClick,
}) {
  const retryLabel = primaryRetryLabelForProfileFailureCategory(row.category);
  const retryDisabled = isProfileFailureRetryDisabled(row) || actionLoading;

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer' }}
      data-item-id={row.itemId}
      onClick={onTableRowClick}
    >
      <TableCell>
        <BreweryBeerCell row={row} onBreweryLinkClick={onBreweryLinkClick} />
      </TableCell>
      <TableCell>
        {formatBeerProfileProcessingFailureCategoryLabel(row.category)}
      </TableCell>
      <TableCell>
        {formatBeerProfileProcessingFailureStageLabel(row.failureStage)}
      </TableCell>
      <TableCell>{formatRelativeCheckTime(row.openedAt) || '—'}</TableCell>
      <TableCell>{truncateProfileFailureError(row.errorMessage)}</TableCell>
      <TableCell>
        <Button
          size="small"
          disabled={retryDisabled}
          data-item-id={row.itemId}
          onClick={onRetryClick}
        >
          {pendingItemId === row.itemId && actionLoading ? '…' : retryLabel}
        </Button>
      </TableCell>
      <TableCell>
        <DispositionActionsCell
          row={row}
          actionLoading={actionLoading}
          onResolveClick={onResolveClick}
        />
      </TableCell>
    </TableRow>
  );
}

FailureTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
  onTableRowClick: PropTypes.func.isRequired,
  onBreweryLinkClick: PropTypes.func.isRequired,
  onRetryClick: PropTypes.func.isRequired,
  onResolveClick: PropTypes.func.isRequired,
};

function FailureTableHeaderRow() {
  return (
    <TableRow>
      <TableCell>Brewery / beer</TableCell>
      <TableCell>Category</TableCell>
      <TableCell>Stage</TableCell>
      <TableCell>Opened</TableCell>
      <TableCell>Error</TableCell>
      <TableCell>Retry</TableCell>
      <TableCell>Disposition</TableCell>
    </TableRow>
  );
}

function FailureTable({
  rows,
  actionLoading,
  pendingItemId,
  onTableRowClick,
  onBreweryLinkClick,
  onRetryClick,
  onResolveClick,
}) {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <FailureTableHeaderRow />
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <FailureTableRow
              key={row.itemId}
              row={row}
              actionLoading={actionLoading}
              pendingItemId={pendingItemId}
              onTableRowClick={onTableRowClick}
              onBreweryLinkClick={onBreweryLinkClick}
              onRetryClick={onRetryClick}
              onResolveClick={onResolveClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

FailureTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
  onTableRowClick: PropTypes.func.isRequired,
  onBreweryLinkClick: PropTypes.func.isRequired,
  onRetryClick: PropTypes.func.isRequired,
  onResolveClick: PropTypes.func.isRequired,
};

export default function BeerProfileProcessingFailuresInbox({
  rows,
  summary,
  loading,
  error,
  actionError,
  actionLoading,
  pendingItemId,
  disposition,
  category,
  failureStage,
  onDispositionChange,
  onCategoryChange,
  onStageChange,
  onRetry,
  onResolve,
  onRefresh,
  pulse,
  trustedBreweryCount,
  pulseLoading,
  pulseError,
}) {
  const [selectedItemId, setSelectedItemId] = useState(null);
  const selectedItem = useMemo(
    () => rows.find((item) => item.itemId === selectedItemId) || null,
    [rows, selectedItemId],
  );

  const handleCloseDetail = useCallback(() => {
    setSelectedItemId(null);
  }, []);

  const handleBreweryLinkClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleTableRowClick = useCallback((event) => {
    const itemId = event.currentTarget.getAttribute('data-item-id');
    if (itemId) {
      setSelectedItemId(itemId);
    }
  }, []);

  const handleRetryClick = useCallback((event) => {
    event.stopPropagation();
    const itemId = event.currentTarget.getAttribute('data-item-id');
    const row = rows.find((item) => item.itemId === itemId);
    if (row) {
      onRetry(row).catch(() => undefined);
    }
  }, [rows, onRetry]);

  const handleResolveClick = useCallback((event) => {
    event.stopPropagation();
    const itemId = event.currentTarget.getAttribute('data-item-id');
    if (itemId) {
      onResolve(itemId).catch(() => undefined);
    }
  }, [onResolve]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={(
          <Button color="inherit" size="small" onClick={onRefresh}>
            Retry
          </Button>
        )}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Increment 1a profile processing failures (retain / shadow rebuild / legacy seed).
        Separate from Inc 1 scrape/monitor failures on the Failures tab.
      </Typography>
      <BeerProfileProcessingPulseStrip
        pulse={pulse}
        trustedBreweryCount={trustedBreweryCount}
        loading={pulseLoading}
        error={pulseError}
      />
      {summary.reportingStale ? (
        <Alert severity="warning">
          Failure list watermark is stale (older than 5 minutes). Refresh or investigate
          writers.
        </Alert>
      ) : null}
      <FilterBar
        disposition={disposition}
        category={category}
        failureStage={failureStage}
        onDispositionChange={onDispositionChange}
        onCategoryChange={onCategoryChange}
        onStageChange={onStageChange}
      />
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {rows.length === 0 ? (
        <Typography color="text.secondary">
          No {disposition} profile processing failures for these filters.
        </Typography>
      ) : (
        <FailureTable
          rows={rows}
          actionLoading={actionLoading}
          pendingItemId={pendingItemId}
          onTableRowClick={handleTableRowClick}
          onBreweryLinkClick={handleBreweryLinkClick}
          onRetryClick={handleRetryClick}
          onResolveClick={handleResolveClick}
        />
      )}
      <FailureDetailDrawer
        item={selectedItem}
        open={Boolean(selectedItem)}
        onClose={handleCloseDetail}
        onRetry={onRetry}
        onResolve={onResolve}
        actionLoading={actionLoading}
        pendingItemId={pendingItemId}
      />
    </Stack>
  );
}

BeerProfileProcessingFailuresInbox.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  summary: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  actionError: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
  disposition: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  failureStage: PropTypes.string.isRequired,
  onDispositionChange: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onStageChange: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  pulse: PropTypes.object.isRequired,
  trustedBreweryCount: PropTypes.number.isRequired,
  pulseLoading: PropTypes.bool.isRequired,
  pulseError: PropTypes.string,
};
